import { useEffect, useState } from 'react';
import Ayuda from '../ui/Ayuda';
import { estados as cargarEstados, indice, reconocer } from './geografia';

/* =====================================================================
   EL SELECTOR DE ESTADO
   =====================================================================
   QUE PROBLEMA RESUELVE. Hoy el estado es texto libre en la ficha de la
   sucursal, en la del sitio, en el cliente que entró por Excel y en el
   interesado que llegó por el bot. Están capturados «Nuevo León», «NUEVO
   LEON», «N.L.» y «nuevo leon», y para cualquier informe por estado son
   cuatro estados distintos.

   =====================================================================
   DEVUELVE EL NOMBRE, NO LA CLAVE. Y ES LA DECISION IMPORTANTE
   =====================================================================
   `onChange` entrega el NOMBRE del estado, tal como se guarda hoy. Así,
   cablear este selector en una pantalla que ya existe NO cambia lo que
   se escribe en la base, no cambia lo que devuelve su API y no obliga a
   migrar ni una fila: la columna sigue siendo el mismo texto, sólo que
   ahora sale de una lista de 32 en vez de un teclado.

   Quien necesite la clave del INEGI o la del SAT las recibe en el
   segundo argumento, que trae el renglón entero del catálogo. Eso es lo
   que hace falta para facturar, y no obliga a nadie más a enterarse.

   =====================================================================
   Y RECONOCE LO QUE YA ESTABA ESCRITO
   =====================================================================
   Si la ficha traía «N.L.», el desplegable abre en Nuevo León. Sin esto,
   abrir una ficha vieja la enseñaría en blanco y guardarla sin tocar el
   campo BORRARIA el estado que tenía — que es la forma más silenciosa
   de perder un dato.

   Lo que no se reconoce se conserva y se ofrece como está, marcado. No
   se tira: el texto raro de alguien es un dato suyo, no basura.
   ===================================================================== */
export default function SelectorEstado({
  valor = '',
  onChange,
  id = 'estado',
  etiqueta = 'Estado',
  requerido = false,
  deshabilitado = false,
}) {
  const [lista, setLista] = useState([]);
  const [fallo, setFallo] = useState(false);

  useEffect(() => {
    let vivo = true;
    cargarEstados()
      .then((filas) => { if (vivo) setLista(filas); })
      .catch(() => { if (vivo) setFallo(true); });
    return () => { vivo = false; };
  }, []);

  const mapa = indice(lista);
  const clave = reconocer(valor, mapa);
  const fila = lista.find((e) => e.clave === clave);
  /* Lo que estaba escrito y no es ninguno de los 32. Se ofrece igual. */
  const ajeno = valor && !fila ? valor : '';

  /* =====================================================================
     SI EL CATALOGO NO CARGA, SE ESCRIBE A MANO
     =====================================================================
     Una lista que no llegó no puede dejar a alguien sin poder guardar su
     dirección. Se cae al campo de texto de siempre, que es lo que había
     antes de este componente: peor que la lista, igual que ayer.
     ===================================================================== */
  if (fallo) {
    return (
      <div>
        <label htmlFor={id} className="block text-sm font-medium mb-1">
          {etiqueta}{requerido && <span className="text-red-600"> *</span>}
        </label>
        <input
          id={id}
          type="text"
          value={valor}
          disabled={deshabilitado}
          onChange={(e) => onChange?.(e.target.value, null)}
          className="w-full rounded-lg border px-3 py-2 text-base"
          placeholder="Escribe el estado"
        />
        <p className="mt-1 text-xs text-gray-600">
          La lista de estados no cargó. Escribe el estado y guarda; se puede
          corregir después.
        </p>
      </div>
    );
  }

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium mb-1">
        {etiqueta}{requerido && <span className="text-red-600"> *</span>}
      </label>
      <select
        id={id}
        value={fila ? fila.nombre : ajeno}
        disabled={deshabilitado || lista.length === 0}
        onChange={(e) => {
          const elegido = lista.find((x) => x.nombre === e.target.value);
          onChange?.(e.target.value, elegido ?? null);
        }}
        /* Objetivo táctil grande: esto se usa con el teléfono en una mano. */
        className="w-full rounded-lg border px-3 py-2 text-base min-h-11"
      >
        <option value="">— Elige el estado —</option>
        {ajeno && (
          <option value={ajeno}>{ajeno} (como estaba escrito)</option>
        )}
        {lista.map((e) => (
          <option key={e.clave} value={e.nombre}>{e.nombre}</option>
        ))}
      </select>
      {/* LA REGLA VA PEGADA AL CONTROL QUE LA EJECUTA, y como DATO: con
          su ancla, su versión y su fecha, para que las pruebas de la
          norma puedan verla. Antes estaba escrita aquí dentro. */}
      {ajeno && <Ayuda id="importador.estado_desconocido" />}
    </div>
  );
}
